import React from "react";
import PremiumQuoteResults from "./QuoteResultsPremium";

const CCG_LONG_DISTANCE_KM = 80;
const CCG_HIGH_FARE_GBP = 120;
const CCG_SL_MARKET_MODE = process.env.REACT_APP_CCG_SL_MARKET_MODE || "enquiry";

const LONDON_AIRPORT_PATTERN = /\b(?:heathrow|gatwick|luton|stansted)(?:\s+airport)?\b/i;
const LONDON_AIRPORT_OUTCODE_PATTERN = /\b(?:TW6|RH6|LU1|LU2|CM24)(?:\s*\d[A-Z]{2})?\b/i;
const SL_POSTCODE_PATTERN = /\bSL\d{1,2}[A-Z]?(?:\s*\d[A-Z]{2})?\b/i;

const isLondonAirportLocation = (location = "") =>
  LONDON_AIRPORT_PATTERN.test(location) || LONDON_AIRPORT_OUTCODE_PATTERN.test(location);

const isCcgSloughMarketQuote = (quote) => {
  const pickup = quote?.request?.pickup || "";
  const dropoff = quote?.request?.dropoff || "";
  const pickupIsSl = SL_POSTCODE_PATTERN.test(pickup);
  const dropoffIsSl = SL_POSTCODE_PATTERN.test(dropoff);
  const pickupIsAirport = isLondonAirportLocation(pickup);
  const dropoffIsAirport = isLondonAirportLocation(dropoff);

  return (
    (pickupIsSl && dropoffIsSl) ||
    (pickupIsSl && dropoffIsAirport) ||
    (pickupIsAirport && dropoffIsSl)
  );
};

const isCcgSloughEnquiryMode = ({ quote }) =>
  CCG_SL_MARKET_MODE === "enquiry" && isCcgSloughMarketQuote(quote);

const ccgPublicPricingPolicy = (card, quote) => {
  if (isCcgSloughEnquiryMode({ quote })) {
    return { visible: false, reason: "ccg_slough_market_enquiry_mode" };
  }
  if (typeof card?.public_price_allowed === "boolean") {
    return {
      visible: card.public_price_allowed,
      reason: card.public_price_allowed ? card.pricing_market || "public_price" : "market_pricing_disabled",
    };
  }
  if (card?.book_mode !== "enquiry") return { visible: true, reason: "live_bookable" };
  if (card?.partner_fare_available === true) return { visible: true, reason: "partner_fare" };

  // Missing metadata means an older backend/quote: retain the existing display during rollout.
  if (card?.partner_fare_available == null) return { visible: true, reason: "legacy_quote" };

  const distanceKm = Number(quote?.distance_km);
  const referenceCard = quote?.vehicles?.find((vehicle) => vehicle.code === "saloon" && vehicle.available !== false);
  const referenceFare = Number(referenceCard?.price ?? card?.price);
  const isLongDistance = Number.isFinite(distanceKm) && distanceKm >= CCG_LONG_DISTANCE_KM;
  const isHighFare = Number.isFinite(referenceFare) && referenceFare > CCG_HIGH_FARE_GBP;

  if (isLongDistance && isHighFare) {
    return { visible: false, reason: "long_distance_no_partner_fare" };
  }
  return { visible: true, reason: "short_or_lower_fare_estimate" };
};

const ccgResultsConfig = {
  brandName: "CheapCabGrab",
  className: "premium-results-ccg",
  funnelTrackingEnabled: true,
  mobileConversionLayout: true,
  chatBrand: "ccg",
  bookingPath: "/book",
  forceEnquiryOnly: isCcgSloughEnquiryMode,
  contactPreferenceEnabled: ({ quote }) =>
    isCcgSloughEnquiryMode({ quote }) ? false : undefined,
  enquiryPrimaryAction: "enquiry",
  enquiryButtonLabel: ({ quote }) =>
    isCcgSloughEnquiryMode({ quote }) ? "Get my accurate quote" : "Request this fare",
  noPriceButtonLabel: ({ quote }) =>
    isCcgSloughEnquiryMode({ quote }) ? "Get my accurate quote" : "Get my fixed price",
  enquiryModalLabel: ({ quote }) =>
    isCcgSloughEnquiryMode({ quote }) ? "Send a quick enquiry" : "Request this fare",
  callbackButtonLabel: ({ quote }) =>
    isCcgSloughEnquiryMode({ quote }) ? "Send a quick enquiry" : "Request callback",
  secondaryEnquiryMode: ({ quote }) =>
    isCcgSloughEnquiryMode({ quote }) ? "quick_enquiry" : "callback",
  secondaryEnquiryModalLabel: "Send a quick enquiry",
  enquiryReassurance: "No payment or obligation. We’ll confirm availability and the final fare before anything is booked.",
  compactEnquiryReassurance: ({ quote }) =>
    isCcgSloughEnquiryMode({ quote })
      ? "No payment or obligation. We only use your details to respond to this journey enquiry."
      : null,
  publicPricingPolicy: ccgPublicPricingPolicy,
  noPriceEyebrow: "This fare needs a quick check",
  noPriceLabel: "Get an accurate fixed quote",
  noPriceSubtext: ({ quote }) =>
    isCcgSloughEnquiryMode({ quote })
      ? "Share passengers and bags. We’ll check our best available rate today."
      : "We’ll check the route and driver availability, then confirm your fixed price.",
  heathrowFriendlyLabels: true,
  routeConfidenceTitle: "Journey check",
  routeConfidenceValue: "Complete",
  routeConfidenceBody: "Route, timing and service coverage assessed from your selected details",
  priorityTitle: "Your journey, handled by airport transfer specialists",
  priorityBody:
    "CheapCabGrab keeps the quote, booking and support flow simple: fixed prices, licensed drivers, flight monitoring and clear handoff before travel.",
  inclusions: [
    "▱ Prices include airport pickup or drop-off charges where shown",
    "◴ Flight monitoring and 60 minutes free airport waiting time",
    "▱ Licensed private-hire drivers and secure booking",
  ],
  proofStats: [
    { value: "1000+", label: "Completed transfers" },
    { value: "24/7", label: "Airport support" },
    { value: "4.8 ★★★★★", label: "Google rating" },
  ],
  trustItems: [
    { icon: "★", title: "4.8 ★★★★★", body: "Google reviews", href: "https://share.google/trIAb3GAvw55L1l8S" },
    { icon: "♢", title: "Fixed upfront fares", body: "No surge pricing" },
    { icon: "✈", title: "Flight monitoring", body: "Airport pickup support" },
    { icon: "♙", title: "Licensed drivers", body: "Private-hire professionals" },
    { icon: "☎", title: "24/7 Support", body: "Before and after booking" },
  ],
  vehicles: {
    saloon: {
      title: "Saloon Car",
      subtitle: "Best for airport transfers, solo travellers and couples",
      image: "/ccg-hero.png",
      badge: "Best value",
      passengers: "Up to 4 Passengers",
      bags: "2 Bags",
      highlight: "Fixed-price taxi transfer with licensed driver and flight monitoring",
      similar: "Toyota Prius, VW Passat or similar",
      proof: "Fixed upfront fare",
      bullets: ["Instant booking where available", "No surge pricing", "Secure online payment"],
    },
    mpv: {
      title: "MPV",
      subtitle: "Extra space for families, luggage and airport journeys",
      image: "/ccg-mpv.png",
      badge: "More space",
      passengers: "Up to 6 Passengers",
      bags: "4 Bags",
      highlight: "Comfortable airport transfer for small groups and extra luggage",
      similar: "Ford Galaxy, VW Sharan or similar",
      proof: "Space for luggage",
      bullets: ["Luggage-friendly", "Flight monitoring", "Licensed driver"],
    },
    nine: {
      title: "8 Seater Minibus",
      subtitle: "Group taxi option for families and teams",
      image: "/minibus-hero.png",
      badge: "Group option",
      badgeTone: "green",
      passengers: "Up to 8 Passengers",
      bags: "Minibus Luggage",
      highlight: "Useful when a standard taxi is too small for passengers and bags",
      similar: "Mercedes Vito, Ford Tourneo or similar",
      proof: "Group-ready option",
      bullets: ["No payment taken for enquiry-only routes", "Group luggage support", "Booking team follow-up"],
    },
    fourteen: {
      title: "16 Seater Minibus",
      subtitle: "Larger minibus for coordinated airport transfers",
      image: "/minibus-hero.png",
      badge: "Large group",
      badgeTone: "rose",
      passengers: "Up to 16 Passengers",
      bags: "Group Luggage",
      highlight: "Best for larger families, work teams, events and multi-passenger airport journeys",
      similar: "Mercedes Sprinter or similar",
      proof: "Confirmation required",
      bullets: ["Availability confirmed before payment", "Group support included", "Flexible journey notes"],
    },
    twentyfive: {
      title: "25+ Seater Coach",
      subtitle: "Coach enquiry for schools, events and large groups",
      image: "/coach-hero.png",
      badge: "Coach hire",
      badgeTone: "rose",
      passengers: "25+ Passengers",
      bags: "Group Luggage",
      highlight: "Coordinated coach option through trusted operator partners",
      similar: "Executive mini coach or similar",
      proof: "Partner allocation confirmed",
      bullets: ["No payment taken now", "Operator confirmation", "Group support included"],
    },
  },
};

const QuoteResultsnew = () => <PremiumQuoteResults config={ccgResultsConfig} />;

export default QuoteResultsnew;
